// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import getColor from "@insite/mobius/utilities/getColor";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Download from "@store/Library/Components/Toolkit/Icons/Download";
import CsvFile from "@store/Library/Components/Icons/Files/CsvFile";
import Mp4File from "@store/Library/Components/Icons/Files/Mp4File";
import JpgFile from "@store/Library/Components/Icons/Files/JpgFile";
import PngFile from "@store/Library/Components/Icons/Files/PngFile";
import PptFile from "@store/Library/Components/Icons/Files/PptFile";
import PdfFile from "@store/Library/Components/Icons/Files/PdfFile";
import TxtFile from "@store/Library/Components/Icons/Files/TxtFile";
import ZipFile from "@store/Library/Components/Icons/Files/ZipFile";
import DefaultFile from "@store/Library/Components/Icons/Files/DefaultFile";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import SelectInline from "@store/Library/Components/Toolkit/SelectInline";
import Pagination from "@insite/mobius/Pagination";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import updateCmsLibraryParameters from "@store/Framework/Store/Store/Data/CmsLibrary/Handlers/UpdateCmsLibraryParameters";
import loadCmsLibraryData from "@store/Framework/Store/Store/Data/CmsLibrary/Handlers/LoadCmsLibrary";
import { linkStyles } from "@store/Themes/common/mixins/link";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import setLibraryFilterNavIsOpen from "@store/Framework/Store/Store/Data/CmsLibrary/Handlers/SetLibraryFilterNavIsOpen";
import Img, { ImgProps } from "@store/MobiusOverrides/Img/Img";

const mapDispatchToProps = {
    updateCmsLibraryParameters,
    loadCmsLibraryData,
    setLibraryFilterNavIsOpen,
};

const mapStateToProps = (state: ApplicationState) => {
    const { currentLibrary, isLoading, libraries, parameters } = state.store.data.cmsLibrary;
    const displayedLibrary = libraries[currentLibrary];

    return {
        isLoading,
        library: displayedLibrary,
        language: state.context.session.language,
        pagination: displayedLibrary?.pagination,
        currentLibrary,
        parameters,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CmsLibraryTilesStyles {
    libraryItemTopSection?: InjectableCss;
    numberOfItems?: TypographyPresentationProps;
    libraryContainer?: GridContainerProps;
    libraryItem?: GridItemProps;
    libraryItemContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    iconWrapper?: InjectableCss;
    fileIcon?: IconPresentationProps;
    fileImg?: ImgProps;
    infosGridItem?: GridItemProps;
    libraryItemName?: TypographyPresentationProps;
    libraryItemLink?: LinkPresentationProps;
    libraryItemFormat?: TypographyPresentationProps;
    libraryItemInfos?: TypographyPresentationProps;
    downloadGridItem?: GridItemProps;
    downloadLink?: LinkPresentationProps;
    filterButton?: ButtonPresentationProps;
    noAssets?: TypographyPresentationProps;
}

export const cmsLibraryTiles: CmsLibraryTilesStyles = {
    libraryItemTopSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: ${pxToRem(15)};
        `,
    },
    numberOfItems: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.small};
        `,
    },
    libraryContainer: {
        gap: 15,
    },
    libraryItem: {
        width: [12, 12, 12, 6, 6],
    },
    libraryItemContainer: {
        gap: 20,
        css: css`
            border: 1px solid ${getColor("common.border")};
            border-radius: ${pxToRem(14)};
            padding: ${pxToRem(15)};
            height: 100%;
        `,
    },
    imageGridItem: {
        width: 3,
    },
    iconWrapper: {
        css: css`
            display: flex;
            position: relative;
            align-items: center;
            justify-content: center;
            width: 100%;
            aspect-ratio: 1/1;
            background: ${getColor("common.accent")};
            overflow: hidden;
        `,
    },
    fileIcon: {
        color: "placeholder",
        css: css`
            width: 50px;
            height: 56px;
            svg {
                width: inherit;
                height: inherit;
            }
        `,
    },
    fileImg: {
        css: css`
            width: 100%;
            height: 100%;
            object-fit: contain;
            display: block;
        `,
    },
    infosGridItem: {
        width: 7,
    },
    libraryItemName: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.large};
            font-weight: ${x => x.theme.design.typography.weight.semibold};
            color: ${getColor("tag.color")};
            display: block;
            margin-bottom: ${pxToRem(10)};
            margin-right: ${pxToRem(8)};
            word-break: normal;
        `,
    },
    libraryItemLink: {
        css: css`
            ${linkStyles("tag.color", "tag.color", false, true)}
        `,
    },
    libraryItemFormat: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.tiny};
            font-weight: ${x => x.theme.design.typography.weight.semibold};
            color: ${getColor("text.accent")};
            background-color: ${getColor("common.accent")};
            padding: ${pxToRem(8)};
            border-radius: ${pxToRem(20)};
            text-transform: uppercase;
        `,
    },
    libraryItemInfos: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smallest};
            color: ${getColor("text.accent")};
        `,
    },
    downloadGridItem: {
        width: 2,
        css: css`
            align-items: flex-end;
            justify-content: flex-end;
        `,
    },
    downloadLink: {
        icon: {
            iconProps: {
                src: Download,
                css: css`
                    color: ${getColor("primary")};

                    &:hover {
                        color: ${getColor("primary.dark")};
                    }
                `,
            },
        },
    },
    filterButton: {
        variant: "secondary",
        css: css`
            ${x => x.theme.button.tertiary.css}
            width: 100%;
            margin-bottom: ${pxToRem(18)};

            ${breakpoint("md")} {
                display: none;
            }
        `,
    },
    noAssets: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
            display: block;
            text-align: center;
            margin-top: ${pxToRem(50)};
        `,
    },
};

const styles = cmsLibraryTiles;

const GetIcon = (extension: string) => {
    switch (extension) {
        case "csv":
            return CsvFile;
        case "mp4":
            return Mp4File;
        case "jpg":
            return JpgFile;
        case "png":
            return PngFile;
        case "ppt":
            return PptFile;
        case "pdf":
            return PdfFile;
        case "txt":
            return TxtFile;
        case "zip":
            return ZipFile;
        default:
            return DefaultFile;
    }
};

const CmsLibraryTiles: FC<Props> = ({
    isLoading,
    library,
    language,
    pagination,
    currentLibrary,
    parameters,
    updateCmsLibraryParameters,
    loadCmsLibraryData,
    setLibraryFilterNavIsOpen,
}) => {
    if (!isLoading && !library) {
        return null;
    }

    const libraryAssets = library?.assets;
    const itemsText = pagination?.totalItemCount === 1 ? translate("Item") : translate("Items");
    const dispatch = useAppDispatch();

    const onChangeSortHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch(
            updateCmsLibraryParameters({ libraryType: currentLibrary, sortBy: event.currentTarget.value, page: 1 }),
        );
    };

    const changePage = (newPageIndex: number) => {
        dispatch(updateCmsLibraryParameters({ ...parameters, libraryType: currentLibrary, page: newPageIndex }));
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        dispatch(
            updateCmsLibraryParameters({ ...parameters, libraryType: currentLibrary, page: 1, pageSize: newPageSize }),
        );
    };

    const openDrawer = () => {
        setLibraryFilterNavIsOpen({ isOpen: true });
    };

    return (
        <>
            <Button onClick={openDrawer} {...styles.filterButton}>
                {translate("Filter")}
            </Button>
            <StyledWrapper {...styles.libraryItemTopSection}>
                <Typography {...styles.numberOfItems}>{`${pagination?.totalItemCount} ${itemsText}`}</Typography>
                <StyledWrapper>
                    {pagination && (
                        <SelectInline
                            label={translate("Sort by")}
                            value={pagination.sortType}
                            labelPosition="left"
                            preventEllipsis={true}
                            onChange={onChangeSortHandler}
                            data-test-selector="cmsLibrarySortSelect"
                        >
                            {pagination.sortOptions.map(sortOption => (
                                <option key={sortOption.sortType} value={sortOption.sortType}>
                                    {sortOption.displayName}
                                </option>
                            ))}
                        </SelectInline>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            {libraryAssets?.length === 0 && (
                <Typography {...styles.noAssets}>
                    {translate("No assets found. Try adjusting your search or filters.")}
                </Typography>
            )}
            {libraryAssets && (
                <>
                    <GridContainer {...styles.libraryContainer}>
                        {libraryAssets.map(asset => (
                            <GridItem {...styles.libraryItem} key={asset.id}>
                                <GridContainer {...styles.libraryItemContainer}>
                                    <GridItem {...styles.imageGridItem}>
                                        {asset.previewUrl.length > 0 ? (
                                            <StyledWrapper {...styles.iconWrapper}>
                                                <Img
                                                    src={`${asset.previewUrl}?width=125&format=jpg`}
                                                    {...styles.fileImg}
                                                />
                                            </StyledWrapper>
                                        ) : (
                                            <StyledWrapper {...styles.iconWrapper}>
                                                <Icon src={GetIcon(asset.extension)} {...styles.fileIcon}></Icon>
                                            </StyledWrapper>
                                        )}
                                    </GridItem>
                                    <GridItem {...styles.infosGridItem}>
                                        <StyledWrapper>
                                            <Typography {...styles.libraryItemName}>
                                                <Link href={asset.downloadUrl} {...styles.libraryItemLink}>
                                                    {asset.name}
                                                </Link>
                                            </Typography>
                                            <>
                                                <Typography {...styles.libraryItemFormat}>{asset.extension}</Typography>
                                                <Typography {...styles.libraryItemInfos}>
                                                    {asset.pageCount > 0 && (
                                                        <>{`${asset.pageCount} ${translate("pages")} - `}</>
                                                    )}
                                                    {asset.lastModified && (
                                                        <>
                                                            {getLocalizedDateTime({
                                                                dateTime: new Date(asset.lastModified),
                                                                language,
                                                            })}
                                                        </>
                                                    )}
                                                    {asset.sizeDisplay && <>{` - ${asset.sizeDisplay}`}</>}
                                                </Typography>
                                            </>
                                        </StyledWrapper>
                                    </GridItem>
                                    <GridItem {...styles.downloadGridItem}>
                                        <Link href={asset.downloadUrl} {...styles.downloadLink} />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        ))}
                    </GridContainer>
                    {pagination && (
                        <Pagination
                            resultsCount={pagination.totalItemCount}
                            currentPage={pagination.page}
                            resultsPerPage={pagination.pageSize}
                            resultsPerPageOptions={pagination.pageSizeOptions}
                            onChangePage={changePage}
                            onChangeResultsPerPage={changeResultsPerPage}
                            prevPage={pagination.prevPageUri}
                            nextPage={pagination.nextPageUri}
                        />
                    )}
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CmsLibraryTiles),
    definition: {
        group: "Common",
        displayName: "CMS Library Tiles",
    },
};

export default widgetModule;
